import { all, takeEvery, put, call, takeLatest } from 'redux-saga/effects'
import store from 'store'
import keyBy from 'lodash/keyBy'
import { login, logout, verifyMfa, forgotPassword, changePassword } from '../../api/auth'
import type { MfaVerifyRequest } from '../../api/auth'
import { getCurrentUser, getUsers } from '../../api/lookups'
import {
  LOGIN_USER,
  FETCH_USER,
  LOGOUT_USER,
  FETCH_USERS,
  MFA_VERIFY,
  FORGOT_PASSWORD,
  CHANGE_PASSWORD,
  AuthStatus,
  UserActionImpl,
  LoginImpl,
  UsersImpl,
  UserPartialImpl,
  LogoutImpl,
  UserGetAction,
  MfaVerifyPayload,
  ForgotPasswordPayload,
  ChangePasswordPayload,
} from './types'
import {
  requestUsers,
  requestUsersSuccess,
  requestUsersError,
  receivedUsers,
  requestUser,
  requestUserSuccess,
  requestUserError,
  receivedUser,
  resetUser,
  getUser,
  mfaRequired,
  mfaVerifySuccess,
  mfaVerifyFailed,
  forgotPasswordSuccess,
  forgotPasswordFailed,
  changePasswordSuccess,
  changePasswordFailed,
} from './actions'
import { showErrorNotification } from '../notifications/actions'
import { deleteCookie } from '../../api/cookie'
import { AxiosResponse, AxiosError } from 'axios'
import { ViewPermissions } from '../../types/AccessControls'
import { saveViewAccess } from '../accessControls/actions'
import { resetAccessControl } from '../accessControls/slice'
import { mixpanelLoginStatusEvent } from "../../tracker/mixpanel/events/user"
import { mixpanelLoginRegister, mixpanelUserLogout } from '../../tracker/mixpanel/mixPanel'

export function* LOGIN({ payload }: UserActionImpl) {
  const { phone, password } = <LoginImpl>payload
  yield put(requestUser())
  try {
    const response: AxiosResponse = yield call(login, phone, password)
    if (response) {
      const { data } = response
      if (data.status === AuthStatus.MFA_REQUIRED) {
        yield put(
          mfaRequired({
            mfaId: data.mfa_id,
            mfaMethod: data.mfa_method,
            mfaSetupRequired: data.mfa_setup_required,
            setupUri: data.setup_uri || null,
            passwordChangeRequired: data.password_change_required || false,
          }),
        )
        yield put(requestUserSuccess())
      } else {
        // MFA not enabled — direct login (status === 'authenticated')
        if (data.token) {
          store.set('token', data.token)
        }
        yield put(getUser())
        yield put(requestUserSuccess())
      }
    }
  } catch (error) {
    mixpanelLoginStatusEvent({ status: "fail", userId: undefined })
    yield put(requestUserError())

    const axiosError = error as AxiosError
    let errorMessage = 'Login failed. Please try again.'

    if (axiosError.response && axiosError.response.data) {
      const errorData = axiosError.response.data as any
      const { detail, message, error: errorMsg } = errorData
      errorMessage = detail || message || errorMsg || 'Phone number / Password is wrong'
    }

    yield put(showErrorNotification(errorMessage))
  }
}

export function* VERIFY_MFA({ payload }: UserActionImpl) {
  const { mfaId, mfaMethod, code, newPassword, confirmPassword } =
    payload as unknown as MfaVerifyPayload
  try {
    const requestPayload: MfaVerifyRequest = {
      mfaId: mfaId,
      mfaMethod: mfaMethod,
      code,
    }
    if (newPassword && confirmPassword) {
      requestPayload.newPassword = newPassword
      requestPayload.confirmPassword = confirmPassword
    }

    const response: AxiosResponse = yield call(verifyMfa, requestPayload)
    if (response) {
      const { data } = response
      if (data.status === AuthStatus.AUTHENTICATED) {
        if (data.token) {
          store.set('token', data.token)
        }
        yield put(mfaVerifySuccess())
        yield put(getUser())
      } else {
        yield put(mfaVerifyFailed('Unexpected response from server. Please try again.'))
      }
    }
  } catch (error) {
    const axiosError = error as AxiosError
    let errorMessage = 'Verification failed. Please try again.'

    if (axiosError.response && axiosError.response.data) {
      const errorData = axiosError.response.data as any
      const { detail, message, error: errorMsg } = errorData
      errorMessage = detail || message || errorMsg || 'Invalid verification code'
    }

    yield put(mfaVerifyFailed(errorMessage))
  }
}

export function* FORGOT_PASSWORD_SAGA({ payload }: UserActionImpl) {
  const { phone, code, newPassword, confirmPassword } =
    payload as unknown as ForgotPasswordPayload
  try {
    const response: AxiosResponse = yield call(forgotPassword, {
      phone,
      code,
      newPassword,
      confirmPassword,
    })
    if (response) {
      const { data } = response
      if (data.status === AuthStatus.PASSWORD_RESET) {
        yield put(forgotPasswordSuccess())
      } else {
        yield put(forgotPasswordFailed('Unexpected response from server. Please try again.'))
      }
    }
  } catch (error) {
    const axiosError = error as AxiosError
    let errorMessage = 'Password reset failed. Please try again.'

    if (axiosError.response && axiosError.response.data) {
      const errorData = axiosError.response.data as any
      const { detail, message, error: errorMsg } = errorData
      errorMessage = detail || message || errorMsg || 'Password reset failed'
    }

    yield put(forgotPasswordFailed(errorMessage))
  }
}

export function* CHANGE_PASSWORD_SAGA({ payload }: UserActionImpl) {
  const { currentPassword, newPassword, confirmPassword, code } =
    payload as unknown as ChangePasswordPayload
  try {
    const response: AxiosResponse = yield call(changePassword, {
      currentPassword,
      newPassword,
      confirmPassword,
      code,
    })
    if (response) {
      const { data } = response
      if (data.status === AuthStatus.PASSWORD_CHANGED) {
        yield put(changePasswordSuccess())
      } else {
        yield put(changePasswordFailed('Unexpected response from server. Please try again.'))
      }
    }
  } catch (error) {
    const axiosError = error as AxiosError
    let errorMessage = 'Password change failed. Please try again.'

    if (axiosError.response && axiosError.response.data) {
      const errorData = axiosError.response.data as any
      const { detail, message, error: errorMsg } = errorData
      errorMessage = detail || message || errorMsg || 'Password change failed'
    }

    yield put(changePasswordFailed(errorMessage))
  }
}

export function* LOAD_CURRENT_ACCOUNT() {
  yield put(requestUser())
  try {
    let data = null
    const cachedUser = store.get('user')

    // Always fetch fresh user data from API to pick up permission/role changes.
    // Auth is cookie-based, so we attempt the call regardless of localStorage token.
    try {
      const response: AxiosResponse = yield call(getCurrentUser)
      data = response.data
      if (!cachedUser) {
        mixpanelLoginStatusEvent({ status: "success", userId: data?.id })
      }
    } catch (apiError) {
      // If API call fails (e.g. not authenticated), fall back to cached user
      if (cachedUser) {
        data = cachedUser
      }
    }

    if (data) {
      yield put(
        receivedUser({
          ...data,
          authorized: true,
        }),
      )
      store.set('user', data)
      const permissions: ViewPermissions = data.permissions
      yield put(saveViewAccess(permissions))
      yield put(requestUserSuccess())
      mixpanelLoginRegister(data)
    } else {
      yield put(requestUserSuccess())
    }
  } catch (e) {
    console.error(e)
    yield put(requestUserError())
  }
}

export function* LOGOUT({ payload }: UserActionImpl) {
  const { shouldCallAPI = true } = <LogoutImpl>payload
  try {
    if (shouldCallAPI) {
      yield call(logout)
      mixpanelUserLogout()
    }
  } catch (e) {
    console.log(e)
  }
  yield call(deleteCookie, 'csrftoken')
  store.remove('token')
  store.remove('user')
  yield put(resetUser())
  yield put(resetAccessControl())
}

export function* GET_USER_LIST({ payload: { filter } }: UserGetAction) {
  yield put(requestUsers())
  try {
    const response: AxiosResponse = yield call(getUsers, filter)
    if (response) {
      const { data }: { data: UserPartialImpl[] } = response
      const userToIdMap = keyBy(data, 'id')
      const allIds = Object.keys(userToIdMap)
      const adminIds: string[] = []
      const specialistIds: string[] = []
      const reviewerIds: string[] = []
      allIds.forEach(id => {
        switch (userToIdMap[id].role) {
          case 'admin': {
            adminIds.push(id)
            break
          }
          case 'content-reviewer': {
            reviewerIds.push(id)
            break
          }
          case 'data-specialist': {
            specialistIds.push(id)
            break
          }
        }
      })
      const payloadToStore: UsersImpl = {
        entities: userToIdMap,
        allIds,
        adminIds,
        reviewerIds,
        specialistIds,
        users: data,
      }
      yield put(receivedUsers(payloadToStore))
      yield put(requestUsersSuccess())
    }
  } catch (error) {
    yield put(requestUsersError())
    yield put(showErrorNotification('Something went wrong while fetching users'))
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(LOGIN_USER, LOGIN),
    takeEvery(MFA_VERIFY, VERIFY_MFA),
    takeEvery(FORGOT_PASSWORD, FORGOT_PASSWORD_SAGA),
    takeEvery(CHANGE_PASSWORD, CHANGE_PASSWORD_SAGA),
    takeEvery(FETCH_USER, LOAD_CURRENT_ACCOUNT),
    takeEvery(LOGOUT_USER, LOGOUT),
    takeLatest(FETCH_USERS, GET_USER_LIST),
    LOAD_CURRENT_ACCOUNT(),
  ])
}
